import { useState, useEffect, useMemo, useCallback } from "react";
import { Search, ArrowUpDown, Tag, ExternalLink, Percent, Save, Loader2, Settings2, ShoppingCart } from "lucide-react";
import { Shimmer, CardGridShimmer } from "@/components/ui/shimmer";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { getAllBrands, type Brand } from "@/utils/brands-api";
import { graphqlRequest } from "@/utils/graphql";
import { toast } from "sonner";

type SortOption = "name-asc" | "name-desc" | "discount-high" | "discount-low";

type CommissionMap = Record<string, number>; // service_id -> commission_percentage
type PlanCommissionMap = Record<string, number>; // plan_id -> commission_percentage

type Plan = {
  id: string;
  plan_name: string | null;
  price: number;
  discounted_price: number;
};

const sortOptions: { value: SortOption; label: string }[] = [
  { value: "name-asc", label: "Name A-Z" },
  { value: "name-desc", label: "Name Z-A" },
  { value: "discount-high", label: "Highest Discount" },
  { value: "discount-low", label: "Lowest Discount" },
];

function getDiscountBadgeVariant(d: number): "default" | "secondary" | "outline" | "destructive" {
  if (d >= 10) return "default";
  if (d >= 5) return "secondary";
  if (d >= 2) return "outline";
  return "secondary";
}

function computePlanDiscount(plan: Plan): number {
  if (!plan.price || plan.price <= 0) return 0;
  return ((plan.price - plan.discounted_price) / plan.price) * 100;
}

/* ── Queries ── */

const FETCH_ALL_COMMISSIONS = `
  query GetAllCommissions($user_id: uuid!) {
    getPlanCommissions(request: { user_id: $user_id }) {
      brand_commissions { whatsub_service_id commission_percentage }
      plan_commissions { whatsub_service_id whatsub_plan_id commission_percentage }
    }
  }
`;

const UPSERT_COMMISSION = `
  mutation UpsertCommission($user_id: uuid!, $whatsub_service_id: uuid!, $commission_percentage: numeric!) {
    upsertClientCommission(request: {
      user_id: $user_id,
      whatsub_service_id: $whatsub_service_id,
      commission_percentage: $commission_percentage
    }) { affected_rows }
  }
`;

const UPSERT_PLAN_COMMISSION = `
  mutation UpsertPlanCommission($user_id: uuid!, $service_id: uuid!, $plan_id: uuid!, $commission: Float!) {
    upsertPlanCommission(request: {
      user_id: $user_id,
      whatsub_service_id: $service_id,
      whatsub_plan_id: $plan_id,
      commission_percentage: $commission
    }) { id commission_percentage affected_rows }
  }
`;

const FETCH_BRAND_PLANS = `
  query BrandPlans($service_id: uuid!) {
    whatsub_plans(
      where: {
        service_id: { _eq: $service_id },
        is_plan: { _eq: false },
        status: { _eq: "active" },
        whatsub_coupon_availability: { count: { _gt: "0" } }
      },
      order_by: { price: asc }
    ) { id plan_name price discounted_price }
  }
`;

// Single batched fetch of every brand's gift-card denominations so we can
// recompute maxDiscount client-side (excluding subscription plans, which the
// shared backend action incorrectly includes).
const FETCH_ALL_GIFT_CARD_PLANS = `
  query AllGiftCardPlans($service_ids: [uuid!]) {
    whatsub_plans(
      where: {
        service_id: { _in: $service_ids },
        is_plan: { _eq: false },
        status: { _eq: "active" },
        whatsub_coupon_availability: { count: { _gt: "0" } }
      }
    ) { service_id price discounted_price }
  }
`;

export default function GiftCardsDiscountPage() {
  const { userId, token } = useAuth();

  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const perPage = 12;
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortOption>("discount-high");

  // Brand-level commissions
  const [commissions, setCommissions] = useState<CommissionMap>({});
  const [editingCommissions, setEditingCommissions] = useState<CommissionMap>({});
  const [savingBrand, setSavingBrand] = useState<string | null>(null);

  // Plan-level commissions — brandId -> planId -> commission
  const [planCommissions, setPlanCommissions] = useState<Record<string, PlanCommissionMap>>({});
  const [editingPlanCommissions, setEditingPlanCommissions] = useState<Record<string, PlanCommissionMap>>({});
  const [savingPlanKey, setSavingPlanKey] = useState<string | null>(null); // `${brandId}::${planId}`

  // Sheet state
  const [openBrand, setOpenBrand] = useState<Brand | null>(null);
  const [brandPlans, setBrandPlans] = useState<Record<string, Plan[]>>({});
  const [loadingPlans, setLoadingPlans] = useState(false);

  // Purchase mode: hides the margin-configuration UI on each brand card and
  // shows a single Purchase CTA instead. Local toggle only — no persistence,
  // no API side-effects. All existing margin state stays intact so flipping
  // back restores the exact same in-progress edits.
  const [purchaseMode, setPurchaseMode] = useState(false);

  // Fetch brands + all commissions (brand + plan) + gift-card plans for
  // client-side maxDiscount recompute. Commissions + plans run in parallel
  // after brands resolve, so total latency = brands + max(commissions, plans).
  useEffect(() => {
    if (!userId || !token) return;
    setLoading(true);

    getAllBrands(userId, token)
      .then(async (brandsData) => {
        const brandIds = brandsData.map((b) => b.brandId);

        const [commissionsData, plansData] = await Promise.all([
          graphqlRequest<{
            getPlanCommissions: {
              brand_commissions: Array<{ whatsub_service_id: string; commission_percentage: number }>;
              plan_commissions: Array<{ whatsub_service_id: string; whatsub_plan_id: string; commission_percentage: number }>;
            };
          }>(FETCH_ALL_COMMISSIONS, { user_id: userId }, token),
          graphqlRequest<{
            whatsub_plans: Array<{ service_id: string; price: number; discounted_price: number }>;
          }>(FETCH_ALL_GIFT_CARD_PLANS, { service_ids: brandIds }, token),
        ]);

        // Group gift-card plans by service_id, compute max base discount
        const maxByService: Record<string, number> = {};
        for (const p of plansData.whatsub_plans) {
          if (!p.price) continue;
          const d = ((p.price - p.discounted_price) / p.price) * 100;
          if (d > (maxByService[p.service_id] ?? 0)) maxByService[p.service_id] = d;
        }

        // Override backend-computed maxDiscount (which incorrectly includes
        // subscription plans) with the gift-card-only max.
        const adjusted = brandsData.map((b) =>
          maxByService[b.brandId] !== undefined
            ? { ...b, maxDiscount: Number(maxByService[b.brandId].toFixed(2)) }
            : b,
        );
        setBrands(adjusted);

        const brandMap: CommissionMap = {};
        for (const c of commissionsData.getPlanCommissions.brand_commissions) {
          brandMap[c.whatsub_service_id] = c.commission_percentage;
        }
        setCommissions(brandMap);
        setEditingCommissions(brandMap);

        const planMap: Record<string, PlanCommissionMap> = {};
        for (const c of commissionsData.getPlanCommissions.plan_commissions) {
          planMap[c.whatsub_service_id] ??= {};
          planMap[c.whatsub_service_id][c.whatsub_plan_id] = c.commission_percentage;
        }
        setPlanCommissions(planMap);
        setEditingPlanCommissions(planMap);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load brands"))
      .finally(() => setLoading(false));
  }, [userId, token]);

  const handleCommissionChange = useCallback((brandId: string, value: string, maxDiscount: number) => {
    const num = parseFloat(value);
    if (value === "" || value === ".") {
      setEditingCommissions((prev) => ({ ...prev, [brandId]: 0 }));
      return;
    }
    if (isNaN(num) || num < 0) return;
    if (num > maxDiscount) return;
    setEditingCommissions((prev) => ({ ...prev, [brandId]: num }));
  }, []);

  const handleSave = useCallback(async (brandId: string) => {
    if (!userId || !token) return;
    const commission = editingCommissions[brandId] ?? 0;

    setSavingBrand(brandId);
    try {
      await graphqlRequest(UPSERT_COMMISSION, {
        user_id: userId,
        whatsub_service_id: brandId,
        commission_percentage: commission,
      }, token);
      setCommissions((prev) => ({ ...prev, [brandId]: commission }));
      toast.success("Brand margin saved");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSavingBrand(null);
    }
  }, [userId, token, editingCommissions]);

  const hasChanged = useCallback((brandId: string) => {
    const saved = commissions[brandId] ?? 0;
    const editing = editingCommissions[brandId] ?? 0;
    return saved !== editing;
  }, [commissions, editingCommissions]);

  const openPlansSheet = useCallback(async (brand: Brand) => {
    setOpenBrand(brand);
    if (brandPlans[brand.brandId]) return;
    setLoadingPlans(true);
    try {
      const data = await graphqlRequest<{ whatsub_plans: Plan[] }>(
        FETCH_BRAND_PLANS, { service_id: brand.brandId }, token ?? "",
      );
      setBrandPlans((prev) => ({ ...prev, [brand.brandId]: data.whatsub_plans ?? [] }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load denominations");
    } finally {
      setLoadingPlans(false);
    }
  }, [brandPlans, token]);

  const handlePlanCommissionChange = useCallback((brandId: string, planId: string, value: string, maxDiscount: number) => {
    const num = parseFloat(value);
    if (value === "" || value === ".") {
      setEditingPlanCommissions((prev) => ({ ...prev, [brandId]: { ...prev[brandId], [planId]: 0 } }));
      return;
    }
    if (isNaN(num) || num < 0) return;
    if (num > maxDiscount) return;
    setEditingPlanCommissions((prev) => ({ ...prev, [brandId]: { ...prev[brandId], [planId]: num } }));
  }, []);

  const handlePlanSave = useCallback(async (brandId: string, planId: string) => {
    if (!userId || !token) return;
    const commission = editingPlanCommissions[brandId]?.[planId] ?? 0;

    const key = `${brandId}::${planId}`;
    setSavingPlanKey(key);
    try {
      await graphqlRequest(UPSERT_PLAN_COMMISSION, {
        user_id: userId,
        service_id: brandId,
        plan_id: planId,
        commission,
      }, token);
      setPlanCommissions((prev) => {
        const next = { ...prev };
        next[brandId] = { ...(next[brandId] ?? {}) };
        if (commission === 0) {
          delete next[brandId][planId];
        } else {
          next[brandId][planId] = commission;
        }
        return next;
      });
      toast.success(commission === 0 ? "Denomination override removed" : "Denomination margin saved");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSavingPlanKey(null);
    }
  }, [userId, token, editingPlanCommissions]);

  const hasPlanChanged = useCallback((brandId: string, planId: string) => {
    const saved = planCommissions[brandId]?.[planId] ?? 0;
    const editing = editingPlanCommissions[brandId]?.[planId] ?? 0;
    return saved !== editing;
  }, [planCommissions, editingPlanCommissions]);

  const filtered = useMemo(() => {
    let result = brands;

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((b) => b.brandName.toLowerCase().includes(q));
    }

    result = [...result].sort((a, b) => {
      switch (sort) {
        case "name-asc": return a.brandName.localeCompare(b.brandName);
        case "name-desc": return b.brandName.localeCompare(a.brandName);
        case "discount-high": return b.maxDiscount - a.maxDiscount;
        case "discount-low": return a.maxDiscount - b.maxDiscount;
      }
    });

    return result;
  }, [brands, search, sort]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  useEffect(() => { setPage(1); }, [search, sort]);

  // Count of per-denomination overrides for a brand (shown as badge on card)
  const getPlanOverrideCount = useCallback((brandId: string) => {
    return Object.keys(planCommissions[brandId] ?? {}).length;
  }, [planCommissions]);

  const currentPlans = openBrand ? (brandPlans[openBrand.brandId] ?? []) : [];
  const brandLevelMargin = openBrand ? (commissions[openBrand.brandId] ?? 0) : 0;

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-1">Gift Cards</h1>
          <p className="text-muted-foreground">
            Configure your margin on each brand. Your customers will see the remaining discount.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={purchaseMode ? "default" : "outline"}
            onClick={() => setPurchaseMode((v) => !v)}
            className="h-8 gap-1.5 px-2.5 text-sm font-medium"
            title="Toggle purchase view — hides margin config, shows a Purchase CTA per brand"
          >
            <ShoppingCart className="h-4 w-4" />
            {purchaseMode ? "Exit Purchase Mode" : "Purchase Mode"}
          </Button>
          <a href="https://docs.superflow.run/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-2.5 py-1 text-sm font-medium hover:bg-muted transition-colors">
            API Docs <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <span className="text-sm text-muted-foreground font-medium">
          {filtered.length} brand{filtered.length !== 1 ? "s" : ""} available
        </span>
        <div className="flex gap-3 items-center">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search brands..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-[220px]"
            />
          </div>
          <Select value={sort} onValueChange={(v) => setSort(v as SortOption)}>
            <SelectTrigger className="w-[180px]">
              <ArrowUpDown className="h-4 w-4 text-muted-foreground mr-1.5" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sortOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Shimmer loading={loading} fallback={<CardGridShimmer />}>
        {paginated.length === 0 ? (
          <div className="flex justify-center py-20">
            <p className="text-muted-foreground">
              {search ? "No brands match your search" : "No brands available"}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {paginated.map((brand) => {
                const myMargin = editingCommissions[brand.brandId] ?? 0;
                const customerDiscount = Math.max(0, brand.maxDiscount - myMargin);
                const isSaving = savingBrand === brand.brandId;
                const changed = hasChanged(brand.brandId);
                const overrideCount = getPlanOverrideCount(brand.brandId);

                return (
                  <Card
                    key={brand.brandId}
                    className={`h-full transition-all duration-200 hover:border-primary hover:-translate-y-0.5 hover:shadow-lg ${changed ? "ring-2 ring-primary/20" : ""}`}
                  >
                    <CardContent className="p-5 flex flex-col h-full">
                      {/* Logo + Discount badge */}
                      <div className="flex items-start justify-between mb-4">
                        <Avatar className="h-13 w-13 rounded-lg border">
                          <AvatarImage src={brand.logoURL} alt={brand.brandName} />
                          <AvatarFallback className="rounded-lg">{brand.brandName[0]}</AvatarFallback>
                        </Avatar>
                        <Badge variant={getDiscountBadgeVariant(brand.maxDiscount)}>
                          <Tag className="h-3 w-3" />
                          {brand.maxDiscount}% off
                        </Badge>
                      </div>

                      {/* Brand name */}
                      <span className="text-sm font-bold mb-3">
                        {brand.brandName.trim()}
                      </span>

                      {purchaseMode ? (
                        // Purchase view — replaces the margin config UI. All
                        // state (editingCommissions, savingBrand, etc.) is
                        // preserved so exiting the mode restores unsaved edits.
                        <div className="flex-1 flex flex-col justify-end">
                          <Button
                            className="w-full h-10 gap-1.5"
                            onClick={() => {
                              // TODO wire to the real purchase flow when it lands.
                              // For now, surface intent visibly so the flow is
                              // easy to test in the meantime.
                              toast.info(`Purchase flow for ${brand.brandName.trim()} — coming soon`);
                            }}
                          >
                            <ShoppingCart className="h-4 w-4" />
                            Purchase
                          </Button>
                        </div>
                      ) : (
                        <>
                          {/* Margin input */}
                          <div className="space-y-2 flex-1">
                            <label className="text-xs font-medium text-muted-foreground">Your Margin (brand-level)</label>
                            <div className="flex items-center gap-2">
                              <div className="relative flex-1">
                                <Input
                                  type="number"
                                  min={0}
                                  max={brand.maxDiscount}
                                  step={0.1}
                                  value={myMargin || ""}
                                  onChange={(e) => handleCommissionChange(brand.brandId, e.target.value, brand.maxDiscount)}
                                  placeholder="0"
                                  className="pr-7 h-9 text-sm"
                                />
                                <Percent className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                              </div>
                              <Button
                                size="sm"
                                variant={changed ? "default" : "outline"}
                                className="h-9 px-2.5"
                                disabled={!changed || isSaving}
                                onClick={() => handleSave(brand.brandId)}
                              >
                                {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                              </Button>
                            </div>
                          </div>

                          {/* Per-denomination button */}
                          <button
                            type="button"
                            onClick={() => openPlansSheet(brand)}
                            className="mt-3 inline-flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
                          >
                            <Settings2 className="h-3.5 w-3.5" />
                            Per-denomination
                            {overrideCount > 0 && (
                              <Badge variant="secondary" className="h-4 px-1.5 text-[10px]">{overrideCount}</Badge>
                            )}
                          </button>

                          {/* Footer - Customer sees */}
                          <div className="flex items-center justify-between mt-3 pt-3 border-t">
                            <span className="text-xs text-muted-foreground">Customer gets</span>
                            <span className={`text-sm font-bold ${customerDiscount > 0 ? "text-primary" : "text-muted-foreground"}`}>
                              {customerDiscount.toFixed(1)}% off
                            </span>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-6">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </div>
            )}
          </>
        )}
      </Shimmer>

      {/* Per-denomination configuration sheet */}
      <Sheet open={!!openBrand} onOpenChange={(v) => { if (!v) setOpenBrand(null); }}>
        <SheetContent className="w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              {openBrand && (
                <Avatar className="h-8 w-8 rounded-md border">
                  <AvatarImage src={openBrand.logoURL} alt={openBrand.brandName} />
                  <AvatarFallback className="rounded-md text-xs">{openBrand.brandName[0]}</AvatarFallback>
                </Avatar>
              )}
              {openBrand?.brandName.trim()} — Denominations
            </SheetTitle>
            <SheetDescription>
              Override margin for specific denominations. Empty or 0 uses brand-level margin ({brandLevelMargin}%).
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-3 overflow-y-auto max-h-[calc(100vh-200px)] pr-1">
            {loadingPlans ? (
              <div className="flex justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : currentPlans.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-10">
                No denominations available for this brand.
              </p>
            ) : (
              currentPlans.map((plan) => {
                if (!openBrand) return null;
                const brandId = openBrand.brandId;
                const baseDiscount = computePlanDiscount(plan);
                const planOverride = editingPlanCommissions[brandId]?.[plan.id];
                const effectiveMargin = planOverride !== undefined && planOverride > 0 ? planOverride : brandLevelMargin;
                const customerDiscount = Math.max(0, baseDiscount - effectiveMargin);
                const key = `${brandId}::${plan.id}`;
                const isSaving = savingPlanKey === key;
                const changed = hasPlanChanged(brandId, plan.id);
                const usesOverride = (planCommissions[brandId]?.[plan.id] ?? 0) > 0;

                return (
                  <div
                    key={plan.id}
                    className={`rounded-lg border p-3 transition-colors ${changed ? "border-primary/40 bg-primary/5" : "bg-background"}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-sm font-semibold">
                          {plan.plan_name?.trim() || `₹${plan.price}`}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Base discount: <span className="font-medium text-foreground">{baseDiscount.toFixed(1)}%</span>
                          {usesOverride && (
                            <Badge variant="outline" className="ml-2 h-4 px-1.5 text-[10px]">override</Badge>
                          )}
                        </p>
                      </div>
                      <span className="text-xs font-medium text-primary">
                        {customerDiscount.toFixed(1)}% to customer
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <Input
                          type="number"
                          min={0}
                          max={baseDiscount}
                          step={0.1}
                          value={planOverride ?? ""}
                          onChange={(e) => handlePlanCommissionChange(brandId, plan.id, e.target.value, baseDiscount)}
                          placeholder={`Brand default: ${brandLevelMargin}%`}
                          className="pr-7 h-9 text-sm"
                        />
                        <Percent className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                      <Button
                        size="sm"
                        variant={changed ? "default" : "outline"}
                        className="h-9 px-2.5"
                        disabled={!changed || isSaving}
                        onClick={() => handlePlanSave(brandId, plan.id)}
                      >
                        {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
